// src/pages/finance-portal/earned-wage-access/EarnedWageAccess.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, Grid,
    Autocomplete, Alert, Skeleton, CircularProgress, Divider,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalculateIcon from '@mui/icons-material/Calculate';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

export default function EarnedWageAccess() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    // Employee search
    const [employees, setEmployees] = useState([]);
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // Form
    const [hoursWorked, setHoursWorked] = useState('');
    const [ewaPercentage, setEwaPercentage] = useState('50');
    const [feeFlat, setFeeFlat] = useState('');
    const [feePercentage, setFeePercentage] = useState('');
    const [financialAccountId, setFinancialAccountId] = useState('');

    // Calculation result
    const [calculation, setCalculation] = useState(null);
    const [calculating, setCalculating] = useState(false);

    // Payout
    const [processing, setProcessing] = useState(false);

    const [error, setError] = useState(null);

    const fetchEmployees = useCallback(async () => {
        setEmployeesLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/payroll/employees', {
                params: { per_page: 200, status: 'ACTIVE' },
            });
            if (res.data.success) {
                setEmployees(res.data.data || []);
            }
        } catch (err) {
            // silent - autocomplete will show empty
        } finally {
            setEmployeesLoading(false);
        }
    }, []);

    useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

    const handleCalculate = async () => {
        if (!selectedEmployee) {
            enqueueSnackbar('Please select an employee', { variant: 'warning' });
            return;
        }
        if (!hoursWorked || parseFloat(hoursWorked) <= 0) {
            enqueueSnackbar('Please enter hours worked', { variant: 'warning' });
            return;
        }

        setCalculating(true);
        setError(null);
        setCalculation(null);
        try {
            const payload = {
                hours_worked: parseFloat(hoursWorked),
                ewa_percentage: parseFloat(ewaPercentage) || 50,
            };
            if (feeFlat) payload.fee_flat = parseFloat(feeFlat);
            if (feePercentage) payload.fee_percentage = parseFloat(feePercentage);

            const res = await axiosInstance.post(
                `/api/finance/payroll/employees/${selectedEmployee.id}/earned-wages`,
                payload
            );
            if (res.data.success) {
                setCalculation(res.data.data || {});
                enqueueSnackbar('Calculation complete', { variant: 'success' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to calculate earned wages';
            setError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setCalculating(false);
        }
    };

    const handleProcessPayout = async () => {
        if (!calculation) {
            enqueueSnackbar('Please calculate earned wages first', { variant: 'warning' });
            return;
        }

        setProcessing(true);
        setError(null);
        try {
            const payload = {
                amount: calculation.payout_amount || calculation.net_payout || 0,
            };
            if (financialAccountId) payload.financial_account_id = financialAccountId;

            const res = await axiosInstance.post(
                `/api/finance/payroll/employees/${selectedEmployee.id}/ewa-payout`,
                payload
            );
            if (res.data.success) {
                enqueueSnackbar('Payout processed successfully', { variant: 'success' });
                // Reset form
                setCalculation(null);
                setHoursWorked('');
                setSelectedEmployee(null);
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to process payout';
            setError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    const resultItems = calculation ? [
        { label: 'Gross Earned', value: formatCurrency(calculation.gross_earned), color: '#1e293b' },
        { label: 'Estimated Taxes', value: formatCurrency(calculation.estimated_taxes), color: '#dc2626' },
        { label: 'Available Amount', value: formatCurrency(calculation.available_amount), color: '#1d4ed8' },
        { label: 'Fee', value: formatCurrency(calculation.fee || calculation.total_fee), color: '#d97706' },
        { label: 'Payout Amount', value: formatCurrency(calculation.payout_amount || calculation.net_payout), color: '#16a34a' },
    ] : [];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <AccountBalanceWalletIcon sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 32 }} />
                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.75rem', color: '#fff' }}>
                            Earned Wage Access
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
                        Calculate and process early wage access payouts for employees
                    </Typography>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

                {/* Employee Selection & Form */}
                <Paper elevation={0} sx={{
                    p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', mb: 3,
                }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 2.5 }}>
                        Employee & Wage Details
                    </Typography>

                    <Grid container spacing={2.5}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={employees}
                                getOptionLabel={(opt) =>
                                    opt ? `${opt.first_name || ''} ${opt.last_name || ''}`.trim() + (opt.employee_id ? ` (${opt.employee_id})` : '') : ''
                                }
                                value={selectedEmployee}
                                onChange={(_, val) => setSelectedEmployee(val)}
                                loading={employeesLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Employee"
                                        placeholder="Type to search..."
                                        size="small"
                                        sx={inputSx}
                                    />
                                )}
                                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Hours Worked"
                                type="number"
                                value={hoursWorked}
                                onChange={(e) => setHoursWorked(e.target.value)}
                                fullWidth
                                size="small"
                                sx={inputSx}
                                inputProps={{ min: 0, step: 0.5 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="EWA Percentage"
                                type="number"
                                value={ewaPercentage}
                                onChange={(e) => setEwaPercentage(e.target.value)}
                                fullWidth
                                size="small"
                                sx={inputSx}
                                inputProps={{ min: 0, max: 100, step: 5 }}
                                helperText="Percentage of earned wages available (default 50%)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Flat Fee ($)"
                                type="number"
                                value={feeFlat}
                                onChange={(e) => setFeeFlat(e.target.value)}
                                fullWidth
                                size="small"
                                sx={inputSx}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Fee Percentage (%)"
                                type="number"
                                value={feePercentage}
                                onChange={(e) => setFeePercentage(e.target.value)}
                                fullWidth
                                size="small"
                                sx={inputSx}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Financial Account ID (optional)"
                                value={financialAccountId}
                                onChange={(e) => setFinancialAccountId(e.target.value)}
                                fullWidth
                                size="small"
                                sx={inputSx}
                                helperText="Destination account for the payout"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                startIcon={calculating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                                onClick={handleCalculate}
                                disabled={calculating}
                                sx={{
                                    fontFamily, fontWeight: 700, borderRadius: '10px',
                                    bgcolor: '#1d4ed8', textTransform: 'none', px: 3,
                                    '&:hover': { bgcolor: '#1e40af' },
                                }}
                            >
                                {calculating ? 'Calculating...' : 'Calculate'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Calculation Result */}
                {calculation && (
                    <Paper elevation={0} sx={{
                        p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', mb: 3,
                    }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 2.5 }}>
                            Calculation Result
                        </Typography>

                        <Grid container spacing={2}>
                            {resultItems.map((item) => (
                                <Grid item xs={12} sm={6} md={4} key={item.label}>
                                    <Card elevation={0} sx={{
                                        p: 2, borderRadius: '12px', border: '1px solid #e2e8f0',
                                        textAlign: 'center',
                                    }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600, mb: 0.5 }}>
                                            {item.label}
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 800, color: item.color }}>
                                            {item.value}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <Divider sx={{ my: 2.5 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <PaymentsIcon />}
                                onClick={handleProcessPayout}
                                disabled={processing}
                                sx={{
                                    fontFamily, fontWeight: 700, borderRadius: '10px',
                                    bgcolor: ACCENT, textTransform: 'none', px: 3,
                                    '&:hover': { bgcolor: '#16a34a' },
                                }}
                            >
                                {processing ? 'Processing...' : 'Process Payout'}
                            </Button>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    );
}
